import {
  MAX_ACTIVE_WEAPONS,
  MOUNTS_BY_TIER,
  MAX_GUN_TIER,
  RANGE_BASE_MUL,
  STARTING_WEAPON,
  SUPERSHOT,
  TOP_MOUNT,
  WEAPONS,
  type Mount,
  type WeaponDef,
} from '@/config/weapons';
import { PLAYER } from '@/config/player';
import { effectiveDamageMul, effectiveFireRate } from './weapon-dps';
import type { BulletSystem } from './bullets';
import type { PlayerStats } from '@/upgrades/stats';
import type { Rng } from '@/core/rng';

/**
 * Currently held weapon + firing logic.
 * Mount layout comes from the GLOBAL gun tier (nose → wings → wings+nose →
 * +top supershot cannon); swapping weapons keeps the tier. docs/09-weapons.md
 */
export class WeaponHolder {
  /** Active weapons, primary first — each fires on its OWN cooldown (G3) */
  equipped: string[] = [STARTING_WEAPON];
  /** Weapon Rack card stacks: each opens one more socket (max 3 active) */
  rackSlots = 0;
  /** Ship-wide mount tier (1..4), raised by the 'barrel' card */
  gunTier = 1;
  private cooldowns: number[] = [0, 0, 0];
  /** Reload length of each socket's last shot — HUD reload fill */
  private periods: number[] = [1, 1, 1];

  /** How many sockets are open right now (1 base + rack stacks). */
  get capacity(): number {
    return Math.min(MAX_ACTIVE_WEAPONS, 1 + this.rackSlots);
  }

  get weaponId(): string {
    return this.equipped[0] ?? STARTING_WEAPON;
  }

  get def(): WeaponDef {
    return WEAPONS[this.weaponId] ?? WEAPONS[STARTING_WEAPON]!;
  }

  get category() {
    return this.def.category;
  }

  /** 0..1 reload progress of socket i (1 = ready to fire). */
  reloadPct(i: number): number {
    const cd = this.cooldowns[i] ?? 0;
    if (cd <= 0) return 1;
    return Math.max(0, 1 - cd / (this.periods[i] ?? 1));
  }

  /** Seconds between shots of socket i at its last trigger. */
  reloadPeriod(i: number): number {
    return this.periods[i] ?? 0;
  }

  get hasTopCannon(): boolean {
    return this.gunTier >= MAX_GUN_TIER;
  }

  private mounts(): ReadonlyArray<Mount> {
    return MOUNTS_BY_TIER[Math.min(this.gunTier, MOUNTS_BY_TIER.length) - 1]!;
  }

  /** Drop ids that are gone / over capacity (death rebuild, scrapping). */
  sanitize(inventory: ReadonlyArray<string>): void {
    this.equipped = this.equipped
      .filter((id) => inventory.includes(id))
      .slice(0, this.capacity);
    if (this.equipped.length === 0 && inventory.length > 0) {
      this.equipped = [inventory[0]!];
    }
  }

  reset(): void {
    this.equipped = [STARTING_WEAPON];
    this.rackSlots = 0;
    this.gunTier = 1;
    this.cooldowns = [0, 0, 0];
    this.periods = [1, 1, 1];
  }

  update(dt: number): void {
    for (let i = 0; i < this.cooldowns.length; i++) this.cooldowns[i]! -= dt;
  }

  tryFire(
    bullets: BulletSystem,
    x: number,
    y: number,
    facing: number,
    aimX: number,
    aimY: number,
    stats: PlayerStats,
    rng: Rng,
  ): boolean {
    // All active weapons fire together, each on its own reload clock; the
    // shared penalty scales with how many sockets are loaded (docs/12 G3)
    let fired = false;
    for (let i = 0; i < this.equipped.length; i++) {
      const def = WEAPONS[this.equipped[i]!];
      if (!def || this.cooldowns[i]! > 0) continue;
      const period = 1 / effectiveFireRate(def, stats, this.equipped.length);
      this.cooldowns[i] = period;
      this.periods[i] = period;
      const damageMul = effectiveDamageMul(def, stats);
      for (const m of this.mounts()) {
        this.fireOne(bullets, x, y, facing, aimX, aimY, m, damageMul, 1, def, stats, rng);
      }
      fired = true;
    }
    return fired;
  }

  /**
   * Supershot: a fan of current-weapon projectiles, damage ×3.
   * At tier 4 it fires from the top cannon with extra punch (docs/09).
   */
  fireSupershot(
    bullets: BulletSystem,
    x: number,
    y: number,
    facing: number,
    aimX: number,
    aimY: number,
    stats: PlayerStats,
    rng: Rng,
  ): void {
    const top = this.hasTopCannon;
    const damageMul =
      SUPERSHOT.damageMul * stats.damageMul * stats.catDamage[this.def.category] *
      stats.supershotMul * (top ? SUPERSHOT.topCannonDamageMul : 1);
    const sizeMul = top ? SUPERSHOT.topCannonScale : SUPERSHOT.scale;
    const mount = top ? TOP_MOUNT : this.mounts()[0]!;

    const fanCount = SUPERSHOT.fanCount + stats.superFanBonus;
    const half = (SUPERSHOT.spreadDeg / 2) * (Math.PI / 180);
    for (let i = 0; i < fanCount; i++) {
      const t = i / Math.max(1, fanCount - 1);
      const offset = -half + t * half * 2;
      this.fireOne(
        bullets, x, y, facing,
        rotX(aimX, aimY, offset), rotY(aimX, aimY, offset),
        mount, damageMul, sizeMul, this.def, stats, rng,
      );
    }
  }

  private fireOne(
    bullets: BulletSystem,
    x: number,
    y: number,
    facing: number,
    aimX: number,
    aimY: number,
    m: Mount,
    damageMul: number,
    sizeMul: number,
    weapon: WeaponDef,
    stats: PlayerStats,
    rng: Rng,
  ): void {
    const p = weapon.projectile;
    const cosF = Math.cos(facing);
    const sinF = Math.sin(facing);
    const mx = x + cosF * m.forward - sinF * m.side;
    const my = y + sinF * m.forward + cosF * m.side;
    const a = m.angleDeg * (Math.PI / 180);
    // Crit rolled per projectile at fire time (docs/12 F1)
    const crit = rng.next() < PLAYER.crit.baseChance + stats.critChanceBonus;
    const critMul = crit ? PLAYER.crit.baseDamageMul + stats.critDamageBonus : 1;

    bullets.spawn({
      x: mx,
      y: my,
      dirX: rotX(aimX, aimY, a),
      dirY: rotY(aimX, aimY, a),
      speed: p.speed * stats.projSpeedMul,
      life: p.life * RANGE_BASE_MUL * stats.rangeMul,
      radius: p.radius * sizeMul,
      damage: p.damage * damageMul * critMul,
      crit,
      friendly: true,
      texKey: p.texKey,
      rotates: p.rotates,
      behavior: p.behavior,
      drag: p.drag,
      turnRate: p.turnRate,
      aoeRadius: p.aoeRadius,
      aoeDamage: p.aoeDamage !== undefined ? p.aoeDamage * damageMul * critMul : undefined,
    });
  }
}

function rotX(x: number, y: number, a: number): number {
  return x * Math.cos(a) - y * Math.sin(a);
}
function rotY(x: number, y: number, a: number): number {
  return x * Math.sin(a) + y * Math.cos(a);
}
